import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { Check, X, Menu } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateIST } from '../utils/dateUtils';

const ChangeRequests = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    loadChangeRequests();
  }, []);

  const loadChangeRequests = async () => {
    try {
      const res = await axiosInstance.get('/change-requests');
      setChangeRequests(res.data);
    } catch (err) {
      console.error('Error loading change requests:', err);
      toast.error(err.response?.data?.detail || 'Error loading change requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (crId) => {
    try {
      await axiosInstance.post(`/change-requests/${crId}/approve`);
      toast.success('Change request approved!');
      loadChangeRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error approving change request');
    }
  };

  const handleReject = async (crId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await axiosInstance.post(`/change-requests/${crId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Change request rejected');
      loadChangeRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error rejecting change request');
    }
  };

  const effectiveRole = user.effective_role || user.role;
  if (effectiveRole !== 'Admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="page-container">
          <div className="text-center py-12">
            <p className="text-red-500">Access Denied: Only Admin can view change requests</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="page-container">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setMenuOpen(true)} 
              className="p-2 hover:bg-amber-100 rounded-xl transition-colors"
              data-testid="open-menu-button"
            >
              <Menu className="w-6 h-6 text-gray-700" />
            </button>
            <h1 className="page-title" data-testid="change-requests-page-title">Change Requests</h1>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
          </div>
        ) : changeRequests.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No pending change requests</p>
          </div>
        ) : (
          <div className="space-y-4 fade-in">
            {changeRequests.map((cr) => (
              <div key={cr.cr_id} className="premium-card p-5" data-testid={`change-request-${cr.cr_id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-lg gold-text">{cr.cr_id}</h3>
                    <p className="text-sm text-gray-600">Order: {cr.order_id}</p>
                  </div>
                  <span className="status-badge status-pending">Pending</span>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Field: {cr.field_name}</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 mb-1">Old Value:</p>
                      <p className="font-semibold text-red-600">{JSON.stringify(cr.old_value)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">New Value:</p>
                      <p className="font-semibold text-green-600">{JSON.stringify(cr.new_value)}</p>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-700 mb-4">
                  <p><strong>Requested by:</strong> {cr.requested_by}</p>
                  <p><strong>Date:</strong> {formatDateIST(cr.created_at)}</p>
                  {cr.reason && <p><strong>Reason:</strong> {cr.reason}</p>}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleApprove(cr.cr_id)}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid={`approve-cr-${cr.cr_id}`}
                  >
                    <Check className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(cr.cr_id)}
                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                    data-testid={`reject-cr-${cr.cr_id}`}
                  >
                    <X className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <SideMenu user={user} isOpen={menuOpen} onClose={() => setMenuOpen(false)} onLogout={onLogout} />
    </div>
  );
};

export default ChangeRequests;
